import { z } from 'zod';
import { CompanyIdSchema } from './company-id';

// =============================================================================
// USER MANAGEMENT SCHEMAS
// Covers: profiles, user_titles, user invitations, and company user access
// =============================================================================

// ===========================
// ENUMS & CONSTANTS
// ===========================

export const USER_ROLES = ['superadmin', 'companies_admin', 'companies_staff'] as const;
export const UserRole = z.enum(USER_ROLES);
export type UserRole = z.infer<typeof UserRole>;

export const PLAN_TYPES = [0, 1, 2, 3] as const;
export const PlanType = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
export type PlanType = z.infer<typeof PlanType>;

// Plan limits: { Free: 3, Essential: 2, Standard: 6, Premium: 15 }
export const PLAN_LIMITS: Record<PlanType, number | null> = {
  0: 3,     // Free: 1 admin + 2 users
  1: 2,     // Essential: 2 users
  2: 6,     // Standard: 6 users
  3: 15,    // Premium: 15 users
};

// Plan names
export const PLAN_NAMES: Record<PlanType, string> = {
  0: 'Free',
  1: 'Essential',
  2: 'Standard',
  3: 'Premium',
};

// Plan prices (in AUD per month ex GST)
export const PLAN_PRICES: Record<PlanType, number | null> = {
  0: null,    // Free
  1: 190,     // Essential
  2: 340,     // Standard
  3: 420,     // Premium
};

// Plan descriptions
export const PLAN_DESCRIPTIONS: Record<PlanType, string> = {
  0: 'Basic features for small teams',
  1: 'Great for getting started',
  2: 'Great for growing farms needing mobile access',
  3: 'For larger teams needing advanced features',
};

// ===========================
// PROFILE SCHEMAS
// ===========================

export const ProfileBaseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  username: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone_number: z.string().nullable(),
  mobile_number: z.string().nullable(),
  address: z.string().nullable(),
  suburb: z.string().nullable(),
  postcode: z.string().nullable(),
  state: z.string().nullable(),
  timezone: z.string().default('Australia/Adelaide'),
  company_id: CompanyIdSchema.nullable(),
  role: UserRole,
  active: z.boolean().default(true),
  permissions: z.array(z.string()).default([]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  deleted_at: z.string().datetime().nullable(),
});

export type Profile = z.infer<typeof ProfileBaseSchema>;

export const ProfileResponseSchema = ProfileBaseSchema;
export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

export const ProfileUpdateSchema = z.object({
  first_name: z.string().max(100).optional(),
  last_name: z.string().max(100).optional(),
  phone_number: z.string().max(50).optional(),
  mobile_number: z.string().max(50).optional(),
  address: z.string().max(255).optional(),
  suburb: z.string().max(100).optional(),
  postcode: z.string().max(20).optional(),
  state: z.string().max(50).optional(),
  timezone: z.string().optional(),
  // Admin-only fields
  role: UserRole.optional(),
  active: z.boolean().optional(),
  title_id: z.number().int().positive().optional(),
});

export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>;

// ===========================
// USER INVITATION SCHEMAS
// ===========================

const JobTitleIdSchema = z.number({
  required_error: 'Job title is required',
  invalid_type_error: 'Job title is required',
}).int().positive();

export const InviteUserRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  mobile_number: z.string().max(50).optional(),
  role: z.enum(['companies_admin', 'companies_staff']),
  // Required: the title decides which dashboard cards the user sees and prices
  // their labour, so nobody may be added without one.
  title_id: JobTitleIdSchema,
});

export type InviteUserRequest = z.infer<typeof InviteUserRequestSchema>;

export const CreateUserRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  password: z.string().min(8, 'Password must be at least 8 characters').max(100),
  mobile_number: z.string().max(50).optional(),
  role: z.enum(['companies_admin', 'companies_staff']),
  title_id: JobTitleIdSchema,
});

export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>;

// A staff member who never signs in (a labourer — job title matching an admin
// "No sign-in — record only" title): no email, no password, no auth user.
export const CreateStaffNoSignInRequestSchema = z.object({
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  mobile_number: z.string().max(50).optional(),
  title_id: JobTitleIdSchema,
});

export type CreateStaffNoSignInRequest = z.infer<typeof CreateStaffNoSignInRequestSchema>;

export const InviteUserResponseSchema = z.object({
  email: z.string().email(),
  message: z.string(),
  expires_in: z.string(),
  created_at: z.string().datetime(),
});

export type InviteUserResponse = z.infer<typeof InviteUserResponseSchema>;

// ===========================
// USER TITLES SCHEMAS
// ===========================

export const UserTitleSchema = z.object({
  id: z.number().int(),
  company_id: z.string().uuid(),
  title: z.string().min(1).max(200),
  pay_rate: z.string().max(211).nullable(),
  // Platform-managed title (the per-company COMPANY ADMIN row). The API refuses
  // to delete or rename it; only its pay rate is editable. The titles screen
  // hides the delete control for these.
  is_system: z.boolean().default(false),
  // Dashboard cards staff on this title may see — route paths from the web
  // app's nav-groups config. Allow-list: an unlisted card is hidden and its page
  // redirects to the dashboard. Company admins ignore it and see every card.
  visible_cards: z.array(z.string()),
  created_by: z.string().nullable(),
  updated_by: z.string().nullable(),
  created_at: z.string().datetime().nullable(),
  updated_at: z.string().datetime().nullable(),
  deleted_at: z.string().datetime().nullable(),
});

export type UserTitle = z.infer<typeof UserTitleSchema>;

/** A dashboard card id as stored in user_titles.visible_cards, e.g. '/ml-hours'. */
export const DashboardCardPathSchema = z.string().regex(/^\/[a-z0-9-]+$/, 'Invalid dashboard card');

export const UserTitleCreateSchema = z.object({
  title: z.string().min(1).max(200),
  pay_rate: z.string().trim().min(1, 'Pay rate is required').max(211),
  company_id: z.string().uuid(),
  visible_cards: z.array(DashboardCardPathSchema).max(200).optional(),
});

export type UserTitleCreate = z.infer<typeof UserTitleCreateSchema>;

export const UserTitleUpdateSchema = UserTitleCreateSchema.partial();

export type UserTitleUpdate = z.infer<typeof UserTitleUpdateSchema>;

export const UserTitlesListResponseSchema = z.array(UserTitleSchema);

export type UserTitlesListResponse = z.infer<typeof UserTitlesListResponseSchema>;

/**
 * What the signed-in user may see on the dashboard. `full_access` is true for a
 * company admin (or a superadmin viewing as the company) — they see every card
 * and `visible_cards` does not apply. Staff with no title get no cards.
 */
export const CardAccessResponseSchema = z.object({
  full_access: z.boolean(),
  title_id: z.number().int().nullable(),
  title: z.string().nullable(),
  visible_cards: z.array(z.string()),
});

export type CardAccessResponse = z.infer<typeof CardAccessResponseSchema>;

// ===========================
// COMPANY SCHEMAS
// ===========================

export const CompanyDetailsSchema = z.object({
  id: z.string().uuid(),
  company_name: z.string().max(350),
  plan_type: PlanType,
  paid_sub: z.boolean().nullable(),
  subscription_id: z.string().nullable(),
  customer_id: z.string().nullable(),
  email_address: z.string().nullable(),
  phone_no: z.string().max(50).nullable(),
  mobile_number: z.string().max(50).nullable().optional(),
  street: z.string().max(200).nullable(),
  suburb: z.string().max(150).nullable(),
  suburb_name: z.string().nullable().optional(),
  postcode: z.string().max(20).nullable(),
  state: z.string().max(20).nullable(),
  state_name: z.string().nullable().optional(),
  country: z.string().max(100).nullable(),
  status: z.string().nullable(),
  // Block-type visibility — which block types this company may add/use.
  block_broadacre: z.boolean().nullable().optional(),
  block_fixed_planting: z.boolean().nullable().optional(),
  block_glasshouse: z.boolean().nullable().optional(),
  block_pivot: z.boolean().nullable().optional(),
  block_fixed_irrigation: z.boolean().nullable().optional(),
  logo_url: z.string().nullable().optional(),
  abn: z.string().nullable().optional(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  lat: z.number().nullable().optional(),
  lon: z.number().nullable().optional(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  stripe_status: z.string().nullable().optional(),
  stripe_trial_end: z.string().nullable().optional(),
  stripe_last_payment: z.string().nullable().optional(),
  // Live plan from the Stripe subscription — the source of truth for the current plan
  // (local plan_type/paid_sub can be stale). stripe_plan_type is the tier (1|2|3) mapped
  // deterministically from the subscription's price id; stripe_plan_name is the display name.
  stripe_plan_name: z.string().nullable().optional(),
  stripe_plan_type: z.number().nullable().optional(),
  stripe_payments: z.array(z.object({
    date: z.string(),
    amount: z.number(),
    currency: z.string(),
    description: z.string().nullable(),
    status: z.string(),
  })).optional(),
});

export type CompanyDetails = z.infer<typeof CompanyDetailsSchema>;

export const CompanyUpdateSchema = z.object({
  company_name: z.string().max(350).optional(),
  email_address: z.string().email().optional(),
  phone_no: z.string().max(50).optional(),
  street: z.string().max(200).optional(),
  suburb: z.string().max(150).optional(),
  postcode: z.string().max(20).optional(),
  state: z.string().max(20).optional(),
  country: z.string().max(100).optional(),
});

export type CompanyUpdate = z.infer<typeof CompanyUpdateSchema>;

// ===========================
// USER LIST SCHEMAS
// ===========================

export const UserListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  username: z.string().nullable(),
  phone_number: z.string().nullable(),
  mobile_number: z.string().nullable().optional(),
  role: UserRole,
  title_id: z.number().int().positive().nullable(),
  title_name: z.string().nullable(),
  // The title's hourly rate, surfaced so the labour picker can show what a
  // person costs — and flag the ones costing nothing. Labour cost is computed
  // live from this (lib/labour-cost.ts), so a null here silently costs $0.
  title_pay_rate: z.string().nullable(),
  /** Contractor workers only: their contractor's name ("Henry Pasin - Contractor 1" on reports). */
  contractor_name: z.string().nullable().optional(),
  active: z.boolean(),
  is_company_admin: z.boolean().default(false),
  /** false = no sign-in (labourer): no email, no auth user. */
  can_sign_in: z.boolean().default(true),
  created_at: z.string().datetime().nullable(),
  last_sign_in_at: z.string().datetime().nullable(),
});

export type UserListItem = z.infer<typeof UserListItemSchema>;

export const PlanInfoSchema = z.object({
  plan_type: PlanType,
  max_users: z.number().int().nullable(),
  current_users: z.number().int(),
  remaining_seats: z.number().int().nullable(),
  max_agronomists: z.number().int().nullable(),
  current_agronomists: z.number().int(),
  remaining_agronomists: z.number().int().nullable(),
  // Company job titles matching an admin "Do not count as part of licence"
  // title. Staff on these take no seat, so they can be added when seats are full.
  exempt_title_ids: z.array(z.number().int()).default([]),
  // Company job titles matching an admin "No sign-in — record only" title
  // (labourers). Staff on these are added with no email or password.
  no_sign_in_title_ids: z.array(z.number().int()).default([]),
});

export type PlanInfo = z.infer<typeof PlanInfoSchema>;

export const UserListResponseSchema = z.object({
  users: z.array(UserListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
  total_pages: z.number().int().nonnegative(),
  plan_info: PlanInfoSchema.optional(),
});

export type UserListResponse = z.infer<typeof UserListResponseSchema>;

// ===========================
// PLAN UPGRADE SCHEMAS
// ===========================

export const UpgradePlanRequestSchema = z.object({
  plan_type: z.union([z.literal(2), z.literal(3)]), // Can only upgrade to Standard (2) or Premium (3)
});

export type UpgradePlanRequest = z.infer<typeof UpgradePlanRequestSchema>;

export const UpgradePlanResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  plan_type: PlanType.optional(),
  stripe_subscription_id: z.string().optional(),
});

export type UpgradePlanResponse = z.infer<typeof UpgradePlanResponseSchema>;

// Available plan detail for upgrade modal
export const AvailablePlanSchema = z.object({
  plan_type: PlanType,
  name: z.string(),
  price: z.number().nullable(),
  max_users: z.number().nullable(),
  description: z.string(),
});

export type AvailablePlan = z.infer<typeof AvailablePlanSchema>;

// ===========================
// ERROR SCHEMAS
// ===========================

export const ApiErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  status: z.number().int(),
  timestamp: z.string().datetime(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export const PlanLimitErrorSchema = ApiErrorSchema.extend({
  error: z.literal('PLAN_LIMIT_EXCEEDED'),
  max_users: z.number().int(),
  current_users: z.number().int(),
});

export type PlanLimitError = z.infer<typeof PlanLimitErrorSchema>;

// ===========================
// QUERY PARAMETER SCHEMAS
// ===========================

export const UserListQuerySchema = z.object({
  active: z.enum(['true', 'false']).optional().transform(v => v === 'true'),
  page: z.string().regex(/^\d+$/).transform(Number).default('1'),
  limit: z.string().regex(/^\d+$/).transform(Number).default('20'),
  search: z.string().optional(),
});

export type UserListQuery = z.infer<typeof UserListQuerySchema>;

export const UserTitlesListQuerySchema = z.object({
  active: z.enum(['true', 'false']).optional().transform(v => v !== 'false'),
  page: z.string().regex(/^\d+$/).transform(Number).optional(),
  limit: z.string().regex(/^\d+$/).transform(Number).optional(),
});

export type UserTitlesListQuery = z.infer<typeof UserTitlesListQuerySchema>;
